import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { useGetActiveUsersQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const ActiveUsersChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error } = useGetActiveUsersQuery({ period });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Active Users</h2>
          <p className="chart-subtitle">Users who have traded or joined in the last 30 days</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend">
            <span className="legend-dot active" />
            <span className="legend-label">Active</span>
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">Failed to load data</div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="activeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #2a2a2a',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                labelFormatter={(label) => formatDate(label)}
                formatter={(value) => [`${value} users`, 'Active']}
              />
              <Area
                type="monotone"
                dataKey="active"
                stroke="#8B5CF6"
                strokeWidth={2}
                fill="url(#activeGradient)"
                name="Active Users"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Current active users: {data.totalActive.toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default ActiveUsersChart;
